// Semi360 Hub SSO bridge for QBuy360 (additive — the local login still works).
//
// On the hosted domain (qbuy360.dev.semi360.ai) the app authenticates against
// the Semi360 Hub: it reads the shared session cookie, exchanges it for a
// short-lived RS256 JWT, and stores that JWT in the same localStorage slot the
// existing API client already sends. Every existing call picks it up unchanged;
// the backend tells the two apart by the token's algorithm.
//
// The session cookie is Domain=.dev.semi360.ai, so it is present on this
// subdomain and `credentials:"include"` sends it cross-origin to the Hub.
//
// This is a port of rfq360/frontend/src/rfq/hub.ts and xref360's lib/hubSso.ts.
// QBuy360 is Create React App, not Vite, so the env vars are REACT_APP_* and
// read from process.env rather than import.meta.env — the behaviour is the same.

import { createHubAuth, localStorageStore } from "@semi360/client/hub-auth";

export const PRODUCT_KEY = "qbuy360";

/** Where the token lives. Must match utils/apiUtils.js and axios-client.js. */
const TOKEN_KEY = "token";

/** Hub origin. Derived from our own hostname so no config is needed in either
 *  environment: qbuy360.<zone> -> hub.<zone>. */
export function hubBaseUrl() {
  const configured = process.env.REACT_APP_HUB_URL;
  if (configured) return configured.replace(/\/$/, "");
  const { protocol, hostname } = window.location;
  const parts = hostname.split(".");
  if (parts.length > 2) return `${protocol}//hub.${parts.slice(1).join(".")}`;
  return "https://hub.semi360.ai";
}

/** Is this the hosted Semi360 environment, so we should drive the Hub flow?
 *
 *  On localhost we keep the plain local-login flow untouched for dev.
 *  REACT_APP_HUB_EMBED=1 forces the hosted/embedded shell on locally too, so the
 *  production chrome can be exercised in dev. It only ever *adds* hosts — real
 *  deploys still match on hostname alone. */
export function hubHosted() {
  if (process.env.REACT_APP_HUB_EMBED === "1") return true;
  return /\.semi360\.ai$/i.test(window.location.hostname);
}

async function getJson(path) {
  try {
    const r = await fetch(hubBaseUrl() + path, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    // Offline, CORS not configured, or no Hub in this environment. All mean
    // "fall back to local login", never "show an error".
    return null;
  }
}

/** The Hub session, or null when it could not be read. */
export async function fetchHubSession() {
  return getJson("/api/session");
}

// Token renewal is the shared @semi360/client (hub-auth): one in-flight
// refresh, expiry-aware getToken(). The token stays in the localStorage slot
// the API client sends as its `token` header; every write is also dispatched
// to redux, because HubGate seeds store.user.token at boot and callers reading
// it would otherwise keep a token that lapsed five minutes in (the Hub BOM
// import failed with "Token expired" until a reload).
const slot = localStorageStore(TOKEN_KEY);
const tokenStore = {
  get: slot.get,
  set: (token) => {
    slot.set(token);
    if (!token) return;
    Promise.all([import("../redux/store"), import("../redux/userSlice")])
      .then(([{ default: store }, { setAuthTokenAction }]) =>
        store.dispatch(setAuthTokenAction(token))
      )
      .catch(() => {
        /* redux not mounted (tests, early boot) — localStorage still holds it */
      });
  },
};

let auth = null;

/** The shared Hub token client, created on first use (needs window). */
export function hubAuth() {
  if (!auth) {
    auth = createHubAuth({ hub: hubBaseUrl(), product: PRODUCT_KEY, store: tokenStore });
  }
  return auth;
}

/** This org's entitlement for QBuy360, or null when it could not be read. */
export async function fetchHubEntitlement() {
  const d = await getJson("/api/entitlements");
  const rows = (d && d.entitlements) || [];
  // The Hub has shipped both spellings of this key; accept either rather than
  // silently reading undefined and gating a paying customer out.
  return (
    rows.find((e) => e.productKey === PRODUCT_KEY || e.product_key === PRODUCT_KEY) || null
  );
}

export function redirectToHubSignin() {
  const next = encodeURIComponent(window.location.href);
  window.location.href = `${hubBaseUrl()}/signin?next=${next}`;
}

/** Load the embeddable Hub header web component script once. */
export function loadHubHeaderScript() {
  if (document.getElementById("semi360-header-embed")) return;
  const s = document.createElement("script");
  s.id = "semi360-header-embed";
  s.src = `${hubBaseUrl()}/embed/header.js`;
  s.async = true;
  document.head.appendChild(s);
}

/**
 * End the session properly.
 *
 * On a Hub domain the Hub owns the session — it lives in a cookie scoped to
 * `.semi360.ai`, and clearing this product's own localStorage does not touch
 * it. Signing out locally would leave the user on a dead local form while the
 * Hub still considered them signed in, so the next product they opened would
 * log them straight back in. Sign out at the Hub, then leave the suite.
 *
 * Returns false when there is no Hub to sign out of (localhost), so the caller
 * keeps its existing local-logout behaviour.
 */
export async function hubSignOut() {
  if (!hubHosted()) return false;
  const base = hubBaseUrl();
  try {
    await fetch(`${base}/api/auth/signout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    /* Sign out locally anyway — never trap someone in a session they asked to
       leave because the Hub was unreachable. */
  }
  try {
    const u = new URL(base);
    window.location.assign(`${u.protocol}//${u.hostname.replace(/^hub\./, "")}`);
  } catch {
    window.location.assign("https://semi360.ai");
  }
  return true;
}

/**
 * The Hub BOM this visit is about, from `?bom=<id>`.
 *
 * The id must be captured before anything navigates away from the entry URL —
 * that is what lets the upload record `hub_bom_id`, and without that the Hub's
 * bom-status call can only ever answer `present: false`, so the board would
 * show that nothing had been ordered no matter how much work happened.
 */
export function hubBomIdFromUrl() {
  try {
    const v = new URLSearchParams(window.location.search).get("bom");
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

const BOM_KEY = "hub_bom_id";

export function rememberHubBomId() {
  const id = hubBomIdFromUrl();
  if (id) {
    try {
      sessionStorage.setItem(BOM_KEY, id);
    } catch {
      /* private mode */
    }
  }
  return id;
}

/** The remembered BOM id, if this session arrived from the Hub. */
export function pendingHubBomId() {
  try {
    return sessionStorage.getItem(BOM_KEY);
  } catch {
    return null;
  }
}

/** Forget the handed-off BOM once it has been imported, so revisiting the
 *  screen in the same tab does not import it a second time. */
export function clearPendingHubBomId() {
  try {
    sessionStorage.removeItem(BOM_KEY);
  } catch {
    /* private mode */
  }
}

/**
 * The Hub project this visit is about, from `?prj=<id>`.
 *
 * The Hub sends both ids on "send to a product": the BOM to work on, and the
 * programme it belongs to. Reading only the BOM loses the context the Hub
 * exists to carry, and leaves the user re-stating facts the Hub already holds.
 */
const PRJ_KEY = "hub_project_id";

export function hubProjectIdFromUrl() {
  try {
    const v = new URLSearchParams(window.location.search).get("prj");
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function rememberHubProjectId() {
  const id = hubProjectIdFromUrl();
  if (id) {
    try {
      sessionStorage.setItem(PRJ_KEY, id);
    } catch {
      /* private mode */
    }
  }
  return id;
}

export function pendingHubProjectId() {
  try {
    return sessionStorage.getItem(PRJ_KEY);
  } catch {
    return null;
  }
}

/** Fetch one Hub BOM with its lines (credentialed; the session cookie authorizes). */
export async function getHubBom(bomId) {
  const d = await getJson(`/api/boms/${encodeURIComponent(bomId)}`);
  return (d && d.bom) || d || null;
}

/** Record that QBuy360 consumed a Hub BOM (feeds the cross-product lifecycle view). */
export async function recordHubBomUsage(bomId, ref) {
  try {
    const fd = new FormData();
    fd.append("product", PRODUCT_KEY);
    fd.append("ref", ref || "");
    await fetch(`${hubBaseUrl()}/api/boms/${encodeURIComponent(bomId)}/usage`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
  } catch {
    /* best-effort */
  }
}
